<template>
  <div v-if="loading">
    <Spinner class="relative h-65vh bg-blue-100" />
  </div>
  <div v-else-if="!loading && !loadingError">
    <HousingStepProductDetail
      :unit="unitData"
      :default-room="defaultRoomData"
      :selected-rooms="selectedRoomsData"
      :show-request-button="false"
      :show-footer="false"
      :show-price="false"
    />
  </div>
  <ErrorPage v-else-if="loadingError" class="relative h-65vh" disable-code />
</template>

<script setup>
import { useQuery } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import ErrorPage from "@/components/errorPage.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import programSessionService from "@/services/programSessions.js";
import unitService from "@/services/unit.js";
import { specialPricingOverrideEntityId, specialPricingOverrideParam } from "@/util/specialPricingOverride";
import HousingStepProductDetail from "./HousingStepProductDetail.vue";

const store = useStore();

const unitData = ref({});
const defaultRoomData = ref({});
const selectedRoomsData = ref([]);
const route = useRoute();

const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");

const sessionId = computed(() => route.query["session-id"]);
const unitId = computed(() => route.query["unit-id"]);
const roomId = computed(() => route.query["room-id"]);
const housingId = computed(() => route.query["housing-id"]);
const loadingError = computed(() => isSessionError.value || isUnitError.value);
const loading = computed(() => isLoadingSession.value || isLoadingUnit.value);

const {
  isLoading: isLoadingSession,
  isError: isSessionError,
  data: sessionData,
} = useQuery({
  queryKey: computed(() => [
    "fetchSession",
    sessionId.value,
    homeInstitutionId.value,
    specialPricingOverrideEntityId(),
  ]),
  queryFn: () =>
    programSessionService.getProgramSession({
      id: sessionId.value,
      home_institution_id: homeInstitutionId.value,
      ...specialPricingOverrideParam(),
    }),
  enabled: !!sessionId.value,
});

const {
  isLoading: isLoadingUnit,
  isError: isUnitError,
  data: housingUnitData,
} = useQuery({
  queryKey: computed(() => ["fetchUnit", housingId.value, unitId.value]),
  queryFn: () => unitService.getUnitById(housingId.value, unitId.value),
  enabled: Boolean(!sessionId.value && housingId.value && unitId.value),
});

watch(sessionData, (newSessionData) => {
  if (newSessionData) {
    const sessionUnits = newSessionData?.data?.data?.items?.session_units ?? [];

    // Filter session_units based on unit_id
    const unit = sessionUnits.find(
      (unit) => unit.unit?.id === unitId.value || unit.unit?.rooms?.find((room) => room.id === roomId.value),
    );

    if (unit) {
      unitData.value = unit.unit;
      defaultRoomData.value = newSessionData?.data?.data?.items?.default_room ?? {};

      if (unit.unit?.rooms?.find((room) => room.id === roomId.value)) {
        selectedRoomsData.value = [roomId.value];
      }
    }
  }
});

watch(housingUnitData, (newHousingUnitData) => {
  if (newHousingUnitData) {
    unitData.value = newHousingUnitData?.data?.data;
  }
});
</script>
